import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { logErr } from "../lib/utils-trel";
import { Award, Copy, Check, QrCode, Users, TrendingUp, DollarSign } from "lucide-react";

const NAVY = "#071A2D";
const EMERALD = "#064E3B";
const GOLD = "#C9A45C";
const IVORY = "#FFF8EE";

const LEVEL_COLORS = {
  bronze:   "#CD7F32",
  silver:   "#A0A0A0",
  gold:     "#C9A45C",
  platinum: "#0EA5E9",
};

/**
 * HostAwards — "Awards & Reach" panel for hosts.
 * QR scans counter, current level, next milestone, referral link, lifetime stats.
 */
export default function HostAwards() {
  const [awards, setAwards] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hosts/awards");
      setAwards(data);
    } catch (e) { logErr("hosts/awards", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!awards) return null;

  const scans = awards?.stats?.qr_scans || 0;
  const cur = awards?.level?.current;
  const nxt = awards?.level?.next;
  const toNext = awards?.level?.to_next || 0;
  const impactTarget = awards?.next_impact_award?.target || 50;
  const impactProgress = awards?.next_impact_award?.progress || 0;

  const referralUrl = awards?.referral?.link ? `${window.location.origin}${awards.referral.link}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(referralUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { logErr("clipboard/host-link", e); }
  };

  return (
    <section data-testid="host-awards" className="mt-10">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Awards & Reach</div>
        <h2 className="font-serif text-2xl sm:text-3xl mt-1" style={{ color: NAVY }}>Grow your guest concierge reach.</h2>
      </div>

      {/* Hero progress card */}
      <div
        className="mt-5 rounded-3xl p-6 sm:p-8 border shadow-luxury overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${IVORY} 0%, #FAEFD8 100%)`, borderColor: `${GOLD}55` }}
        data-testid="host-awards-hero"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${GOLD}22`, color: "#8a6a2e" }}>
            <Award size={26}/>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
              {cur ? "Current level" : "Next milestone"}
            </div>
            <div className="font-serif mt-1" style={{ color: NAVY, fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)" }}>
              {cur?.name || "Host Impact Award"}
            </div>
            <div className="text-sm mt-1" style={{ color: "rgba(7,26,45,0.65)" }}>
              {nxt ? (
                <>You&apos;re <b>{toNext}</b> QR scans away from <b>{nxt.name}</b>.</>
              ) : (
                <>You&apos;ve reached the top level. Keep growing your reach.</>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "rgba(7,26,45,0.55)" }}>QR Scans</div>
            <div className="font-serif text-4xl" style={{ color: NAVY }} data-testid="host-qr-scans">{scans.toLocaleString()}</div>
          </div>
        </div>

        {/* Impact Award progress (every 50 scans) */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs" style={{ color: "rgba(7,26,45,0.6)" }}>
            <span>Next Host Impact Award</span>
            <span><b>{impactProgress}</b> / 50 scans · target {impactTarget}</span>
          </div>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "rgba(7,26,45,0.10)" }}>
            <div className="h-full transition-all" style={{ width: `${Math.min(100, (impactProgress / 50) * 100)}%`, background: GOLD }}/>
          </div>
        </div>
      </div>

      {/* Lifetime stats */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={QrCode} label="QR scans" value={scans.toLocaleString()}/>
        <Stat icon={Users} label="Signups" value={awards.stats.qr_signups || 0}/>
        <Stat icon={TrendingUp} label="Bookings generated" value={awards.stats.qr_bookings || 0}/>
        <Stat icon={DollarSign} label="Total earned" value={`$${Number(awards.stats.total_earned || 0).toLocaleString()}`}/>
      </div>

      {/* Milestones strip */}
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="host-milestones">
        {awards.milestones.map((m) => {
          const reached = scans >= m.min;
          const accent = LEVEL_COLORS[m.key] || GOLD;
          return (
            <article
              key={m.key}
              className="rounded-2xl p-4 border"
              style={{
                background: reached ? `${accent}10` : "#fff",
                borderColor: reached ? `${accent}66` : "rgba(7,26,45,0.10)",
              }}
              data-testid={`host-milestone-${m.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
                  <Award size={16}/>
                </div>
                {reached && <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: accent }}>Earned</span>}
              </div>
              <div className="mt-3 font-medium" style={{ color: NAVY }}>{m.name}</div>
              <div className="text-xs mt-0.5" style={{ color: "rgba(7,26,45,0.55)" }}>{m.min}+ QR scans</div>
            </article>
          );
        })}
      </div>

      {/* Referral / QR */}
      <div
        className="mt-6 rounded-3xl p-5 sm:p-7 border bg-white"
        style={{ borderColor: "rgba(7,26,45,0.10)" }}
        data-testid="host-referral-card"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${EMERALD}10`, color: EMERALD }}>
            <QrCode size={22}/>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium" style={{ color: NAVY }}>Your guest concierge link</div>
            <div className="text-sm mt-1" style={{ color: "rgba(7,26,45,0.65)" }}>
              Share this with every guest. Each scan grows your reach and progresses you toward the next host level.
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <code className="px-3 py-1.5 rounded-lg text-xs font-mono bg-[#FFF8EE] border" style={{ borderColor: "rgba(7,26,45,0.10)", color: NAVY }} data-testid="host-referral-code">
                {awards.referral.code}
              </code>
              <button
                onClick={copy}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-[#FFF8EE]"
                style={{ borderColor: "rgba(7,26,45,0.10)", color: NAVY }}
                data-testid="host-referral-copy"
              >
                {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy guest link</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border px-4 py-3" style={{ borderColor: "rgba(7,26,45,0.08)" }}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "rgba(7,26,45,0.55)" }}>
        <Icon size={12}/> {label}
      </div>
      <div className="font-serif text-2xl mt-1" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}
